import { useEffect, useMemo, useState } from 'react'
import { ExternalLink, FileText, Link2, Save, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { FINANCE_DETAIL_FIELD_CLASS } from '@/components/Financeiro/FinanceDetailSectionCard'
import { financeService, type FinanceContractRow, type FinanceRequestDetail } from '@/services/financeService'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import { cn } from '@/utils/cn'

type Props = {
  detail: FinanceRequestDetail
  canManage: boolean
  onReload?: () => void
  showDeliveryMode?: boolean
}

function deliveryModeLabel(mode: unknown) {
  if (mode === 'link') return 'Link do contrato'
  if (mode === 'pdf') return 'PDF do contrato'
  return '—'
}

function unsignedContracts(contracts: FinanceContractRow[] = []) {
  return contracts.filter((c) => c.type === 'nao_assinado')
}

export function FinanceC4ContractDeliverablesSection({
  detail,
  canManage,
  onReload,
  showDeliveryMode = true,
}: Props) {
  const formData =
    detail.formData && typeof detail.formData === 'object'
      ? (detail.formData as unknown as Record<string, unknown>)
      : {}

  const projectId = detail.project?.id
  const savedLink = typeof formData.contractSignatureLink === 'string' ? formData.contractSignatureLink : ''
  const deliveryMode = formData.contractDeliveryMode
  const drafts = useMemo(
    () => unsignedContracts(detail.project?.contracts),
    [detail.project?.contracts],
  )

  const [link, setLink] = useState(savedLink)
  const [savingLink, setSavingLink] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    setLink(savedLink)
  }, [savedLink])

  const handleSaveLink = async () => {
    setSavingLink(true)
    try {
      await financeService.updateC4Deliverables(detail.id, { contractSignatureLink: link })
      toast.success('Link de assinatura salvo')
      onReload?.()
    } catch {
      toast.error('Não foi possível salvar o link')
    } finally {
      setSavingLink(false)
    }
  }

  const handleUploadPdf = async () => {
    if (!file || !projectId) return
    setUploading(true)
    try {
      await financeService.uploadContract(projectId, file, 'nao_assinado')
      toast.success('PDF do contrato (não assinado) anexado')
      setFile(null)
      onReload?.()
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Falha ao enviar PDF'))
    } finally {
      setUploading(false)
    }
  }

  const hasDeliverables = Boolean(savedLink) || drafts.length > 0

  return (
    <div className='space-y-5'>
      {showDeliveryMode && (
        <div className='rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3'>
          <p className='text-[10px] font-semibold uppercase tracking-wider text-text-muted'>
            Formato solicitado pelo closer
          </p>
          <p className='mt-1 text-sm font-medium text-text-primary'>
            {deliveryModeLabel(deliveryMode)}
          </p>
        </div>
      )}

      <div className='grid gap-4 lg:grid-cols-2'>
        <div className='rounded-xl border border-white/[0.08] bg-white/[0.02] p-4'>
          <div className='mb-3 flex items-center gap-2'>
            <div
              className='flex h-9 w-9 items-center justify-center rounded-lg'
              style={{ backgroundColor: 'rgba(99, 102, 241, 0.12)', color: '#6366f1' }}
            >
              <Link2 size={16} aria-hidden />
            </div>
            <div>
              <p className='text-sm font-semibold text-text-primary'>Link para assinatura</p>
              <p className='text-xs text-text-muted'>Cole o link da plataforma de assinatura eletrônica</p>
            </div>
          </div>

          {canManage ? (
            <div className='space-y-3'>
              <input
                type='url'
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder='https://...'
                className={FINANCE_DETAIL_FIELD_CLASS}
              />
              <Button
                type='button'
                size='sm'
                disabled={savingLink || link.trim() === savedLink}
                onClick={() => void handleSaveLink()}
              >
                <Save size={14} />
                {savingLink ? 'Salvando…' : 'Salvar link'}
              </Button>
            </div>
          ) : savedLink ? (
            <a
              href={savedLink}
              target='_blank'
              rel='noreferrer'
              className='inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline'
            >
              <ExternalLink size={14} />
              Abrir link de assinatura
            </a>
          ) : (
            <p className='text-sm text-text-muted'>Aguardando o financeiro disponibilizar o link.</p>
          )}
        </div>

        <div className='rounded-xl border border-white/[0.08] bg-white/[0.02] p-4'>
          <div className='mb-3 flex items-center gap-2'>
            <div
              className='flex h-9 w-9 items-center justify-center rounded-lg'
              style={{ backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}
            >
              <FileText size={16} aria-hidden />
            </div>
            <div>
              <p className='text-sm font-semibold text-text-primary'>PDF do contrato (não assinado)</p>
              <p className='text-xs text-text-muted'>Documento para revisão e assinatura do cliente</p>
            </div>
          </div>

          {drafts.length > 0 ? (
            <ul className='mb-3 space-y-2'>
              {drafts.map((c) => (
                <li
                  key={c.id}
                  className='flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.03] p-3'
                >
                  <FileText size={16} className='shrink-0 text-amber-400' aria-hidden />
                  <div className='min-w-0 flex-1'>
                    <p className='text-sm font-medium text-text-primary'>Versão {c.version}</p>
                    <p className='text-xs text-text-muted'>
                      {c.uploadedBy?.name ?? 'Financeiro'} ·{' '}
                      {new Date(c.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <a
                    href={resolvePublicAssetUrl(c.fileUrl)}
                    target='_blank'
                    rel='noreferrer'
                    className='shrink-0 text-xs font-semibold text-accent hover:underline'
                  >
                    Abrir PDF
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className={cn('text-sm text-text-muted', canManage && 'mb-3')}>
              Nenhum PDF anexado ainda.
            </p>
          )}

          {canManage && !projectId && (
            <p className='rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-sm text-amber-200'>
              Este chamado ainda não está vinculado a um projeto financeiro. Avance o deal para Gerar Contrato ou
              contate o suporte para vincular o projeto antes de anexar o PDF.
            </p>
          )}

          {canManage && projectId && (
            <div className='rounded-lg border border-dashed border-white/[0.12] bg-white/[0.02] p-3'>
              <input
                type='file'
                accept='application/pdf,.pdf'
                className={`${FINANCE_DETAIL_FIELD_CLASS} mb-3 file:mr-3 file:rounded file:border-0 file:bg-accent/15 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-accent`}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <Button
                type='button'
                size='sm'
                disabled={!file || uploading}
                onClick={() => void handleUploadPdf()}
              >
                <Upload size={14} />
                {uploading ? 'Enviando…' : 'Anexar PDF não assinado'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {!canManage && !hasDeliverables && (
        <p className='rounded-lg border border-dashed border-white/[0.1] px-4 py-5 text-center text-sm text-text-muted'>
          O financeiro ainda está preparando o contrato. Você será notificado quando o link ou o PDF estiverem disponíveis.
        </p>
      )}
    </div>
  )
}
